// src/components/Tables/TableManager.jsx
import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import Toolbar from "./Toolbar";
import Column from "./Column";
import TablesList from "./TablesList";
import TableEditor from "./TableEditor";
import NewTableDialog from "./NewTableDialog";
import './TableManager.css'
import {
  fetchTables,
  addTable,          // likely used inside NewTableDialog
  editTable,
  importTables,
  exportTables,
  openTableTab,
  closeTableTab,
  forceCloseTable,
  deleteTable,      // 🔴 NEW: delete
} from "../../services/tablesApi";

import { exportTableQrCodesZip } from '../../utils/tableQrCodes';
import { useAuth } from "../../auth/AuthContext";

const genId = () => Math.random().toString(36).slice(2, 10);

const adaptTable = (t) => {
  const id = String(t.id ?? t.table_id ?? t.tableID ?? t.number ?? genId());
  const name =
    t.name ??
    t.table_name ??
    t.tableName ??
    t.nickname ??
    (t.number ? `Table ${t.number}` : `Table ${id}`);
  return { id, name, ...t };
};

const adaptPayloadToTables = (payload) => {
  const raw = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.tables)
    ? payload.tables
    : Array.isArray(payload?.categories)
    ? payload.categories
    : [];
  return { tables: raw.map(adaptTable) };
};

const TableManager = ({ restaurantId, loggedInUser_Username }) => {
  const { tenantServerUrl } = useAuth();

  const [db, setDb] = useState({ tables: [] });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [selectedTableId, setSelectedTableId] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [newOpen, setNewOpen] = useState(false); // Add-table dialog
  const [exportingQrCodes, setExportingQrCodes] = useState(false);

  const lastGoodRef = useRef(db);

  const selectedTable = useMemo(
    () => db.tables.find((t) => t.id === selectedTableId) || null,
    [db, selectedTableId]
  );

  const load = async () => {
    if (!tenantServerUrl) {
      setErr("No tenant server URL available");
      return;
    }

    setLoading(true);
    setErr(null);

    try {
      const payload = await fetchTables(tenantServerUrl);
      const adapted = adaptPayloadToTables(payload);
      setDb(adapted);
      lastGoodRef.current = adapted;

      const hasSelected = adapted.tables.some(
        (t) => t.id === selectedTableId
      );
      const nextId = hasSelected
        ? selectedTableId
        : adapted.tables[0]?.id ?? null;
      setSelectedTableId(nextId);
    } catch (e) {
      setErr(e?.message || "Failed to load tables");
      if (lastGoodRef.current) setDb(lastGoodRef.current);
    } finally {
      setLoading(false);
    }
  };

  // Reload whenever tenantServerUrl changes (e.g. user switches restaurant)
  useEffect(() => {
    if (!tenantServerUrl) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantServerUrl]);

  const handleRefresh = load;

  const handleImport = async (jsonText) => {
    try {
      await importTables(jsonText);
    } finally {
      await load();
    }
  };

  const handleExport = async () => {
    const json = await exportTables();
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "tables.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportQrCodes = async () => {
    if (!restaurantId) {
      alert("Restaurant ID is required to export QR codes.");
      return;
    }
    if (!db.tables.length) {
      alert("No tables to export.");
      return;
    }

    setExportingQrCodes(true);
    try {
      const count = await exportTableQrCodesZip({
        tables: db.tables,
        restaurantId,
      });
      console.log(`Exported ${count} table QR code(s).`);
    } catch (e) {
      alert(`Export QR codes failed: ${e?.message || e}`);
    } finally {
      setExportingQrCodes(false);
    }
  };

  // Show modal instead of prompt
  const handleAddTable = () => {
    setNewOpen(true);
  };

  // Called by NewTableDialog on success
  const handleCreated = async (createdRow) => {
    await load();
    const newId = String(
      createdRow?.id ?? createdRow?.table_id ?? createdRow?.number ?? ""
    );
    if (newId) setSelectedTableId(newId);
    setEditorOpen(true);
  };

  const handleEditTable = () => {
    if (!selectedTable) return alert("Select a table first");
    setEditorOpen(true);
  };

  const tableEditorRef = useRef(null);

  // click on row → open editor and scroll to it
  const handleSelect = (t) => {
    setSelectedTableId(t.id);
    setEditorOpen(true);
  };

  const handleSave = async (updatedFields) => {
    if (!selectedTable || !tenantServerUrl) return;
    await editTable(
      tenantServerUrl,
      selectedTable.id,
      selectedTable.number,
      updatedFields
    );
    await load();
    setEditorOpen(false);
  };

  const handleOpenTab = async (tbl) => {
    if (!tenantServerUrl) return;
    try {
      await openTableTab(tenantServerUrl, {
        tableId: tbl.id,
        number: tbl.number,
        restaurantId,
        tabType: tbl.tabType,
        bywho: loggedInUser_Username,
      });
      await load();
    } catch (e) {
      alert(`Open Tab failed: ${e.message}`);
    }
  };

  const handleCloseTab = async (tbl) => {
    if (!tenantServerUrl) return;
    try {
      await closeTableTab(tenantServerUrl, {
        number: tbl.number,
        bywho: loggedInUser_Username,
      });
      await load();
    } catch (e) {
      alert(`Close Tab failed: ${e.message}`);
    }
  };

  const handleForceClose = async (tbl) => {
    if (!tenantServerUrl) return;
    try {
      await forceCloseTable(tenantServerUrl, {
        number: tbl.number,
        bywho: loggedInUser_Username,
        waiter: "Moemedi Rakhudu",
        total: 0,
      });
      await load();
    } catch (e) {
      alert(`Force Close failed: ${e.message}`);
    }
  };

  const banner = (
    <div
      style={{
        padding: "8px 12px",
        background: err ? "#ffecec" : "#f7f7f7",
        border: `1px solid ${err ? "#ffb3b3" : "#ddd"}`,
        borderRadius: 8,
        margin: "8px 12px",
      }}
    >
      <strong>Selected:</strong>{" "}
      {selectedTable ? (
        <>
          {selectedTable.nickname ?? selectedTable.name}
          {" · "}
          <em>Status:</em> {selectedTable.status ?? "—"}
          {" · "}
          <em>Occupants:</em> {selectedTable.occupants ?? "0"} /{" "}
          {selectedTable.capacity ?? selectedTable.occupants}
          {" · "}
          <em>Table Action:</em> {selectedTable.action ?? "—"}
        </>
      ) : (
        "None"
      )}
      {loading && (
        <span style={{ marginLeft: 10, opacity: 0.6 }}>Loading…</span>
      )}
      {err && (
        <span style={{ marginLeft: 10, color: "#b30000" }}>({err})</span>
      )}
      {!tenantServerUrl && (
        <span style={{ marginLeft: 10, color: "#b30000" }}>
          (No tenant server URL)
        </span>
      )}
    </div>
  );

  const [isToolbarMinimized, setToolbarMinimized] = useState(false);
  const scrollThreshold = 100;
  const onScroll = useCallback(() => {
    const y = typeof window !== "undefined" ? window.scrollY ?? document.documentElement.scrollTop : 0;
    setToolbarMinimized(y > scrollThreshold);
  }, []);
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [onScroll]);

  useEffect(() => {
    if (editorOpen && selectedTable && tableEditorRef.current) {
      tableEditorRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      const timer = setTimeout(() => window.scrollBy({ top: -75, behavior: "smooth" }), 400);
      return () => clearTimeout(timer);
    }
  }, [editorOpen, selectedTable]);

  return (
    <div className="table-manager-root">
      <div className={`table-manager-toolbar ${isToolbarMinimized ? "table-manager-toolbar--minimized" : ""}`}>
        <Toolbar
          onRefresh={handleRefresh}
          onImport={handleImport}
          onExport={handleExport}
          onExportQrCodes={handleExportQrCodes}
          exportingQrCodes={exportingQrCodes}
          onAddTable={handleAddTable}
          onEditTable={handleEditTable}
        />
        <button
          type="button"
          className="table-manager-toolbar-expand"
          aria-label="Expand table actions"
          onClick={() => setToolbarMinimized(false)}
        >
          ▼ Show actions
        </button>
      </div>

      <div className={`table-manager-content ${isToolbarMinimized ? "table-manager-content--toolbar-minimized" : ""}`}>
      {banner}

      <div className="table-manager-layout"
        style={{
          display: "flex",
          gap: 12,
          background: "#f1f1f1",
          padding: 12,
        }}
      >
        <Column>
          <TablesList
            tables={db.tables}
            selectedTableId={selectedTableId}
            onSelect={handleSelect}
            onOpenTab={handleOpenTab}
            onCloseTab={handleCloseTab}
            onForceClose={handleForceClose}
          />
        </Column>

        {/* Right-side editor drawer */}
        {editorOpen && selectedTable && (
          <div ref={tableEditorRef} className="table-manager-editor" id="table-editor"
            style={{
              width: 550,
              background: "#fff",
              border: "1px solid #e5e5e5",
              borderRadius: 12,
              padding: 16,
              position: "sticky",
              top: 12,
              alignSelf: "flex-start",
              boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
            }}
          >
            <TableEditor
              table={selectedTable}
              restaurantId={restaurantId}
              onCancel={() => setEditorOpen(false)}
              tenantServerUrl = {tenantServerUrl}
              onSave={handleSave}
              // These use the ctx from TableEditor to call tenant-aware APIs
              onOpenTab={async (_formValues, ctx) => {
                if (!tenantServerUrl) return;
                await openTableTab(tenantServerUrl, {
                  tableId: ctx.tableId ?? ctx.id ?? selectedTable.id,
                  number: ctx.tableNumber ?? ctx.number ?? selectedTable.number,
                  restaurantId,
                  tabType: ctx.tabType,
                  bywho: ctx.bywho ?? loggedInUser_Username,
                });
                await load();
              }}
              onCloseTab={async (_formValues, ctx) => {
                if (!tenantServerUrl) return;
                await closeTableTab(tenantServerUrl, {
                  number: ctx.tableNumber ?? ctx.number ?? selectedTable.number,
                  bywho: ctx.bywho ?? loggedInUser_Username,
                });
                await load();
              }}
              onForceCloseTable={async (_formValues, ctx) => {
                if (!tenantServerUrl) return;
                await forceCloseTable(tenantServerUrl, {
                  number: ctx.tableNumber ?? ctx.number ?? selectedTable.number,
                  bywho: ctx.bywho ?? loggedInUser_Username,
                  waiter: _formValues?.waiter ?? "",
                  total: _formValues?.total ?? 0,
                });
                await load();
              }}
              // 🔴 NEW: Delete Table hook
              onDeleteTable={async (_formValues, ctx) => {
                if (!tenantServerUrl) return;
                try {
                  await deleteTable(tenantServerUrl, {
                    id: ctx.tableId ?? ctx.id ?? selectedTable.id,
                    number:
                      ctx.tableNumber ?? ctx.number ?? selectedTable.number,
                    restaurantId,
                    bywho: ctx.bywho ?? loggedInUser_Username,
                  });
                  // After delete, reload and close editor
                  // load() will also re-pick a selected table if any remain
                  await load();
                  setEditorOpen(false);
                } catch (e) {
                  alert(`Delete Table failed: ${e.message}`);
                }
              }}
            />
          </div>
        )}
      </div>
      </div>

      {/* Add Table dialog */}
      <NewTableDialog
        open={newOpen}
        restaurantId={restaurantId}
        tenantServerUrl={tenantServerUrl} // so dialog can call addTable(tenantServerUrl, ...)
        onClose={() => setNewOpen(false)}
        onCreated={handleCreated}
      />
    </div>
  );
};

export default TableManager;
